<script setup lang="ts">
/**
 * /resume-synonyms — the hub for the per-word guides. Answers "better words
 * for a resume" in a paragraph, then lists every word, verbs and adjectives
 * apart, because the fix differs: a weak verb gets a stronger verb, a weak
 * adjective usually gets evidence instead.
 */
import { RESUME_SYNONYMS, synonymWord } from '~/constants/resumeSynonyms'

const { t, locale } = useI18n()

definePageMeta({
  layout: false,
  auth: false,
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/synonyma-do-zivotopisu', de: '/synonyme-lebenslauf', pl: '/synonimy-do-cv' } })

const groups = computed(() => [
  { id: 'verbs', items: RESUME_SYNONYMS.filter(item => item.kind !== 'adjective') },
  { id: 'adjectives', items: RESUME_SYNONYMS.filter(item => item.kind === 'adjective') },
].map(group => ({
  ...group,
  items: group.items
    .map(item => ({ ...item, label: synonymWord(item, locale.value) }))
    .sort((a, b) => a.label.localeCompare(b.label, locale.value)),
})))
</script>

<template>
  <MarketingFeaturePage
    seo-key="resumeSynonyms"
    path="/resume-synonyms"
    :breadcrumb-title="t('resumeSynonyms.hub.heading')"
  >
    <div class="sofax">
      <section class="bg-white dark:bg-gray-950">
        <div class="section-space-sm">
          <div class="shell">
            <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
              <div class="mb-3 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
                {{ t('resumeSynonyms.hub.shortLabel') }}
              </div>
              <p class="text-lg">{{ t('resumeSynonyms.hub.short') }}</p>
            </div>
          </div>
        </div>
      </section>

      <section
        v-for="(group, index) in groups"
        :key="group.id"
        :class="index === 0 ? 'bg-white dark:bg-gray-950' : 'surface-alt'"
      >
        <div :class="index === 0 ? 'section-space-bottom' : 'section-space'">
          <div class="shell">
            <SofaxSectionHeading
              :eyebrow="t(`resumeSynonyms.hub.${group.id}.eyebrow`)"
              :title="t(`resumeSynonyms.hub.${group.id}.title`)"
              :text="t(`resumeSynonyms.hub.${group.id}.lead`)"
              width="max-w-4xl"
            />
            <ul class="mx-auto grid max-w-5xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <li v-for="item in group.items" :key="item.slug">
                <LocaleLink
                  :to="`/resume-synonyms/${item.slug}`"
                  class="block rounded-[0.625rem] px-5 py-4 font-semibold text-ink hover:bg-accent-soft/20 dark:text-white"
                  :class="index === 0 ? 'bg-surface dark:bg-gray-800' : 'bg-white dark:bg-gray-800'"
                >
                  {{ t('resumeSynonyms.hub.itemLabel', { word: item.label }) }}
                </LocaleLink>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section class="bg-white dark:bg-gray-950">
        <div class="section-space">
          <div class="shell">
            <div class="mx-auto max-w-3xl text-center">
              <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('resumeSynonyms.hub.next.title') }}</h2>
              <p class="mt-5 text-lg">{{ t('resumeSynonyms.hub.next.p1') }}</p>
              <div class="mt-8">
                <SofaxBtn to="/free-cv-builder" :label="t('resumeSynonyms.page.cta')" variant="accent" />
              </div>
              <p class="mt-6">
                <LocaleLink to="/blog/boost-your-resume-with-100-powerful-action-verbs" class="link">{{ t('resumeSynonyms.hub.next.verbsLink') }} →</LocaleLink>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </MarketingFeaturePage>
</template>
